import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { caseStudies } from '../content/editorial';

const Section = styled.section`
  padding: 24px 0 104px;
  background: #efecf0;
`;

const Inner = styled.div`
  width: min(1380px, calc(100% - 2rem));
  margin: 0 auto;
  display: grid;
  gap: 2.25rem;
`;

const Header = styled.div`
  display: grid;
  gap: 0.9rem;
  max-width: 46rem;

  @media (max-width: 760px) {
    justify-items: center;
    text-align: center;
  }
`;

const Eyebrow = styled.span`
  display: inline-flex;
  width: fit-content;
  color: rgba(23, 33, 47, 0.64);
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const Title = styled.h2`
  margin: 0;
  color: #17212f;
  font-size: clamp(2.35rem, 5vw, 4rem);
  line-height: 0.96;
  letter-spacing: -0.04em;
  text-wrap: balance;
`;

const Deck = styled.p`
  margin: 0;
  max-width: 42rem;
  color: #465263;
  font-size: 1rem;
  line-height: 1.65;
`;

const ShowcaseCard = styled.article`
  display: grid;
  grid-template-columns: minmax(300px, 0.92fr) minmax(0, 1.08fr);
  gap: clamp(1.5rem, 4vw, 3.5rem);
  align-items: center;
  padding: 2rem 0;
  border-top: 1px solid rgba(23, 33, 47, 0.12);
  border-bottom: 1px solid rgba(23, 33, 47, 0.12);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Visual = styled.div`
  min-height: clamp(320px, 42vw, 460px);
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VisualImage = styled.img`
  width: 100%;
  height: auto;
  max-height: clamp(320px, 42vw, 460px);
  object-fit: contain;
  display: block;
`;

const Content = styled.div`
  display: grid;
  align-content: start;
  gap: 1.1rem;
  max-width: 34rem;
`;

const StudyTitle = styled.h3`
  margin: 0;
  color: #17212f;
  font-size: clamp(1.9rem, 3vw, 2.8rem);
  line-height: 0.96;
  letter-spacing: -0.04em;
  text-wrap: balance;
`;

const StudyDeck = styled.p`
  margin: 0;
  color: #17212f;
  font-size: 1.08rem;
  line-height: 1.65;
`;

const StudyNote = styled.p`
  margin: 0;
  color: #465263;
  font-size: 0.98rem;
  line-height: 1.7;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding-top: 0.45rem;
`;

const ActionLink = styled(Link)`
  width: fit-content;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0.78rem 1.15rem;
  border-radius: 999px;
  border: 1px solid rgba(23, 33, 47, 0.14);
  background: rgba(255, 255, 255, 0.68);
  color: #17212f;
  font-size: 0.96rem;
  font-weight: 700;
  text-decoration: none;
  transition:
    background-color 160ms ease,
    color 160ms ease,
    border-color 160ms ease,
    transform 160ms ease;

  &:hover {
    color: #5b4120;
    border-color: rgba(91, 65, 32, 0.45);
    background: rgba(255, 255, 255, 0.92);
    transform: translateY(-1px);
  }
`;

export default function CaseStudySection() {
  const featuredStudy = caseStudies.at(0);

  if (!featuredStudy) {
    return null;
  }

  return (
    <Section>
      <Inner>
        <Header>
          <Eyebrow>Case Study</Eyebrow>
          <Title>Telling the Tale of Legendary Businesses</Title>
          <Deck>
            One recent project from the studio, with a direct path to the full case studies
            archive.
          </Deck>
        </Header>

        <ShowcaseCard>
          <Visual>
            <VisualImage
              src="/images/grdPrimary.png"
              alt={`${featuredStudy.client} homepage preview`}
            />
          </Visual>

          <Content>
            <StudyTitle>{featuredStudy.title}</StudyTitle>
            <StudyDeck>{featuredStudy.deck}</StudyDeck>
            <StudyNote>
              Visit the works page to explore the full archive of CyBard case studies and recent
              client projects.
            </StudyNote>

            <Actions>
              <ActionLink to="/works">Visit Case Studies</ActionLink>
            </Actions>
          </Content>
        </ShowcaseCard>
      </Inner>
    </Section>
  );
}
